import { createSelector } from 'reselect';
import { DEFAULT_SECTION_ACCESS_TYPE, SECTION_ACCESS_TYPES, VIEW_ONLY_SECTIONS } from '../utils/consts';

export const getNavigationState = state => state.navigation;

export const getNavigationSections = createSelector([getNavigationState], navigationState => navigationState?.sections);

export const getFeatureSectionsByName = (navigationSections, appFeatureName) => {
    if (!navigationSections) return [];
    const feature = navigationSections.flatMap(section => section.elements).find(({ name }) => name === appFeatureName);
    return feature?.sections || [];
};

export const getFeatureSectionsByLink = (navigationSections, pathname) => {
    if (!navigationSections) return [];
    const feature = navigationSections.flatMap(section => section.elements).find(({ link }) => link === pathname);
    return feature?.sections || [];
};

const getSectionAccessTypeValue = section => (section ? section.access_type : DEFAULT_SECTION_ACCESS_TYPE);

export const deriveSectionsAccess = sections => {
    if (sections.length === 0)
        return {
            accessType: DEFAULT_SECTION_ACCESS_TYPE,
            isSingleAccessLevel: true,
        };

    const hasViewAccess = sections.some(
        ({ access_type: accessType, name }) =>
            accessType === SECTION_ACCESS_TYPES.VIEW && !VIEW_ONLY_SECTIONS.includes(name)
    );
    const hasEditAccess = sections.some(({ access_type: accessType }) => accessType === SECTION_ACCESS_TYPES.EDIT);
    const isSingleAccessLevel = (hasEditAccess && !hasViewAccess) || (hasViewAccess && !hasEditAccess);
    return {
        accessType: hasEditAccess ? SECTION_ACCESS_TYPES.EDIT : SECTION_ACCESS_TYPES.VIEW,
        isSingleAccessLevel,
    };
};

export const getSectionAccessType = (appFeatureName, sectionName) =>
    createSelector([getNavigationSections], navigationSections => {
        const sections = getFeatureSectionsByName(navigationSections, appFeatureName);
        const section = sections.find(({ name }) => name === sectionName);
        return getSectionAccessTypeValue(section);
    });

export const getFeatureSectionsMap = appFeatureName =>
    createSelector([getNavigationSections], navigationSections => {
        const sections = getFeatureSectionsByName(navigationSections, appFeatureName);
        return sections.reduce((acc, section) => {
            acc[section.name] = getSectionAccessTypeValue(section);
            return acc;
        }, {});
    });

export const getCanAccessFeature = appFeatureName =>
    createSelector([getNavigationSections], navigationSections => {
        if (!navigationSections) return false;
        const feature = navigationSections
            .flatMap(section => section.elements || [])
            .find(({ name }) => name === appFeatureName);
        return !!feature;
    });
